import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { $profileFeatureStore } from '@lib/store/profile';
import { $cardLimitsStore, $cardStore } from '@lib/store/card';
import { useCardFlow, useCurrencySymbol } from '@shared/hooks';
import { BlockReason, Frequency, type UpdateLimitCardRequest } from '@shared/api/rest/cards';
import { PageContainer } from '@shared/widgets/pageContainer';
import { AccountInfo, AccountInfoRow } from '@widgets';
import { AddUsers } from '@widgets/card/addUsers/addUsers';
import { TransactionsTable } from '@widgets/transaction/transactionsTable';
import { CardSidebar } from '@widgets/card/cardSidebar/cardSidebar';
import { NoData, Spinner } from '@shared/widgets';

export const CardPage = () => {
  const { id } = useParams<{ id: string }>();

  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const cardStore = useUnit($cardStore.store);
  const cardLimitsStore = useUnit($cardLimitsStore.store);

  const cardFlow = useCardFlow(profileFeatureStore.data?.cardFlows, id ? parseInt(id) : undefined);
  const symbol = useCurrencySymbol(cardStore.data?.currency);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!id) return;
    const idNumber = parseInt(id);
    $cardStore.events.getCard(idNumber);

    const currentCardPermission = profileFeatureStore.data?.cardFlows.find((x) => x.cardId === idNumber);
    if (currentCardPermission?.viewCardLimit) {
      $cardLimitsStore.events.getCardLimits(idNumber);
    }
  }, [id]);

  const balance = useMemo<number | undefined>(() => {
    let amount = 0;
    if (!cardLimitsStore.data) return undefined;
    const frequency = cardLimitsStore.data.frequency;
    if (frequency === Frequency.DAILY) {
      amount = cardLimitsStore.data.dayLimits?.otcAmount || 0;
    } else if (frequency === Frequency.CYCLE) {
      amount = cardLimitsStore.data.cycleLimits?.otcAmount || 0;
    } else if (frequency === Frequency.YEAR) {
      amount = cardLimitsStore.data.yearLimits?.otcAmount || 0;
    }

    return Math.min(cardLimitsStore.data.currentOTCAmount - amount, cardLimitsStore.data.currentOTBAmount);
  }, [cardLimitsStore.data]);

  const freezeCardHandler = useCallback(async () => {
    try {
      setIsLoading(true);
      const request = cardStore.data!.cardId;
      await $cardStore.events.freezeCard({ request });
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  }, [$cardStore, cardStore.data?.cardId]);
  const unfreezeCardHandler = useCallback(async () => {
    try {
      setIsLoading(true);
      const request = cardStore.data!.cardId;
      await $cardStore.events.unfreezeCard({ request });
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  }, [$cardStore, cardStore.data?.cardId]);
  const remindPinHandler = useCallback(
    async (password: string) => {
      try {
        setIsLoading(true);
        const request = { cardId: cardStore.data!.cardId, password: password };
        await $cardStore.events.remindPin({ request });
        setIsLoading(false);
      } catch {
        setIsLoading(false);
      }
    },
    [$cardStore, cardStore.data?.cardId],
  );
  const updateLabelCardHandler = useCallback(
    async (cardLabel: string) => {
      try {
        setIsLoading(true);
        const request = { cardId: cardStore.data!.cardId, cardLabel: cardLabel };
        await $cardStore.events.updateLabelCard({ request });
        setIsLoading(false);
      } catch {
        setIsLoading(false);
      }
    },
    [$cardStore, cardStore.data?.cardId],
  );
  const updateCategoryCardHandler = useCallback(
    async (category: string) => {
      try {
        setIsLoading(true);
        const request = { cardId: cardStore.data!.cardId, category: category };
        await $cardStore.events.updateCategoryCard({
          request,
        }),
          setIsLoading(false);
      } catch {
        setIsLoading(false);
      }
    },
    [$cardStore, cardStore.data?.cardId],
  );
  const updateLimitCardHandler = useCallback(
    async (newLimits: UpdateLimitCardRequest) => {
      try {
        setIsLoading(true);
        newLimits.cardUUId = cardStore.data!.cardGuid;
        await $cardLimitsStore.events.updateLimitCard({ request: newLimits });
        setIsLoading(false);
      } catch {
        setIsLoading(false);
      }
    },
    [$cardStore, cardStore.data?.cardGuid],
  );
  const reportHandler = useCallback(
    async (reason: BlockReason, location?: string) => {
      try {
        setIsLoading(true);
        const request = {
          cardId: cardStore.data!.cardId,
          blockReason: reason,
          location: location,
        };
        await $cardStore.events.reportLostStolen({ request });
        setIsLoading(false);
      } catch {
        setIsLoading(false);
      }
    },
    [$cardStore, cardStore.data?.cardId],
  );
  const activateHandler = useCallback(async () => {
    try {
      setIsLoading(true);
      const request = cardStore.data!.cardId;
      await $cardStore.events.activate({ request });
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  }, [$cardStore, cardStore.data?.cardId]);

  return (
    <PageContainer title='Card' backText='Back to all cards'>
      {cardStore.loading ? (
        <Spinner position />
      ) : !cardStore.data ? (
        <NoData />
      ) : (
        <div className='flex'>
          <div className='flex flex-col flex-auto min-w-0'>
            <div className='mb-12'>
              <AccountInfo
                title={cardStore.data.cardLabel}
                type1={cardStore.data.productDisplayName}
                user={cardStore.data.cardHolder}
                amount={balance}
                status={cardStore.data.status}
                type2='Subject to Account Availability'
                icon={symbol}
              >
                <AccountInfoRow divider />
                <AccountInfoRow text='Card number' number={cardStore.data.cardNumber} />
                <AccountInfoRow text='Account number' number={cardStore.data.accountNumber} />
              </AccountInfo>
            </div>
            {profileFeatureStore.data?.userManagement.managerAccountsOrCardsPermissions && id && <AddUsers cardId={id} />}
            {cardFlow?.viewTransactions && <TransactionsTable cardId={cardStore.data.cardId} />}
          </div>
          <div className='flex-none w-80 ml-8'>
            <CardSidebar
              card={cardStore.data}
              limits={cardLimitsStore.data || undefined}
              loading={isLoading}
              onFreezeCard={freezeCardHandler}
              onUnFreezeCard={unfreezeCardHandler}
              onActivate={activateHandler}
              onUpdateLabel={updateLabelCardHandler}
              onRemindPin={remindPinHandler}
              onReport={reportHandler}
              onUpdateLimits={updateLimitCardHandler}
              onUpdateCategory={updateCategoryCardHandler}
            />
          </div>
        </div>
      )}
    </PageContainer>
  );
};
